/*
 * cluutch-charly-hotel.js : page catégorie « Hôtel Charly » (gabarit collection.hotel-charly).
 * 1. Apparitions à l'entrée dans l'écran (`data-charly-anim` : monte, rideau, cascade, fondu,
 *    lignes) : rien n'est masqué avant que ce script ne pose `html.charly-anime` ; une image ne se
 *    dévoile qu'une fois décodée ; les masques tombent à la fin du mouvement (`.fin`).
 * 2. Sommaire : entrée active et filet de progression, chapitre par chapitre (le collant et les
 *    ancres sont en CSS : position sticky, scroll-padding-top, scroll-margin-top).
 * 3. Bandeau des teintes : défilement continu, plus vif quand on défile, arrêt au survol et par le
 *    bouton pause.
 * 4. Film : le plan large bouge lentement tant qu'il est à l'écran.
 * 5. Fenêtre vidéo : la vidéo du <template> voisin du bouton, clonée au tap, lancée, puis mise en
 *    pause et retirée à la fermeture ; rien n'est chargé avant.
 * 6. Bouton d'achat d'un produit pas encore publié : au clic, il dit la date de mise en vente,
 *    puis reprend son libellé.
 * Chaque section relie ce script : la garde n'en garde qu'une exécution. Dans l'éditeur de thème,
 * rien n'est masqué (`html.charly-editeur`) et une section rechargée est reprise.
 * Auteur : Cluutch / Studio Hogo.
 */
(() => {
  'use strict';
  if (window.cluutchCharlyHotel) return;
  window.cluutchCharlyHotel = true;

  const racine = document.documentElement;
  const calme = window.matchMedia('(prefers-reduced-motion: reduce)');
  const editeur = Boolean(window.Shopify && window.Shopify.designMode);
  if (editeur) racine.classList.add('charly-editeur');

  /* 1. Apparitions : une seule fois ; en cascade quand plusieurs entrent ensemble */
  const enAttente = new Set();
  const cibleDe = new Map();
  let io = null;
  const estRideau = (el) => el.dataset.charlyAnim === 'rideau';
  // un rideau est découpé par son propre clip-path, que Chromium applique aussi à l'observation :
  // on observe son parent, qui ne l'est pas
  const sonde = (el) => (estRideau(el) ? el.parentElement : el);
  const finir = (el) => {
    el.querySelectorAll('.cluutch-charly-l').forEach((l) => l.classList.add('fin'));
    if (estRideau(el)) el.classList.add('fin');
  };
  const montrer = (el) => {
    // une rangée observée d'un bloc (qui défile en largeur) dévoile ses cadres en cascade
    if (el.dataset.charlyAnim === 'cascade') {
      [...el.children].forEach((enfant, i) => {
        enfant.style.setProperty('--i', Math.min(i, 6));
        enfant.classList.add('vu');
      });
    }
    el.classList.add('vu');
    window.setTimeout(() => finir(el), 2400);   // filet de sécurité si la fin du mouvement n'arrive pas
  };
  // une image ne se dévoile qu'une fois décodée (jamais un cadre vide après un saut du sommaire) ;
  // si elle attendait son chargement différé, elle part tout de suite
  const pret = (el) => {
    const im = el.tagName === 'IMG' ? el : el.querySelector('img');
    if (!im || (im.complete && im.naturalWidth)) return Promise.resolve();
    im.loading = 'eager';
    return Promise.race([
      im.decode ? im.decode().catch(() => {}) : Promise.resolve(),
      new Promise((r) => { window.setTimeout(r, 1500); })
    ]);
  };
  const reveler = (el) => {
    if (!enAttente.delete(el)) return;
    if (io) io.unobserve(sonde(el));
    pret(el).then(() => montrer(el));
  };
  const toutReveler = () => { [...enAttente].forEach(reveler); };

  const preparer = (portee) => {
    const cibles = [];
    portee.querySelectorAll('[data-charly-anim]').forEach((el) => {
      if (el.dataset.charlyAnim !== 'cascade') { cibles.push(el); return; }
      // une rangée qui défile en largeur se dévoile d'un bloc : ses cadres hors écran ne seraient
      // jamais « vus » avant d'être glissés
      if (window.getComputedStyle(el).overflowX !== 'visible') cibles.push(el);
      else cibles.push(...el.children);
    });
    cibles.forEach((el) => {
      if (el.classList.contains('vu')) return;
      if (el.getBoundingClientRect().bottom < 0) { montrer(el); return; }
      enAttente.add(el);
      const s = sonde(el);
      if (s !== el) cibleDe.set(s, el);
      io.observe(s);
    });
  };

  if (!editeur && !calme.matches && 'IntersectionObserver' in window) {
    io = new IntersectionObserver((entrees) => {
      entrees.filter((e) => e.isIntersecting).map((e) => cibleDe.get(e.target) || e.target)
        .sort((a, b) => (a.compareDocumentPosition(b) & Node.DOCUMENT_POSITION_FOLLOWING ? -1 : 1))
        .forEach((el, k) => {
          if (el.parentElement && el.parentElement.dataset.charlyAnim === 'cascade') {
            el.style.setProperty('--i', Math.min(k, 6));
          }
          reveler(el);
        });
    }, { rootMargin: '0px 0px -10% 0px', threshold: 0 });
    racine.classList.add('charly-anime');
    preparer(document);
    // un élément atteint au clavier avant son apparition se révèle aussitôt (le focus ne tombe
    // jamais sur un élément transparent)
    document.addEventListener('focusin', (e) => {
      for (let el = e.target; el && el !== document.body; el = el.parentElement) {
        if (enAttente.has(el)) reveler(el);
      }
    });
    // fin d'un mouvement : les masques tombent, le repos est celui des planches
    document.addEventListener('transitionend', (e) => {
      const t = e.target;
      if (e.propertyName === 'transform' && t.parentElement && t.parentElement.classList.contains('cluutch-charly-l')) {
        t.parentElement.classList.add('fin');
      }
      if (e.propertyName === 'clip-path' && t.dataset && estRideau(t)) t.classList.add('fin');
    });
  }

  /* 2. Sommaire : la ligne de lecture est le bas de la marge des ancres (en-tête et sommaire) ; un
        chapitre commence là où son ancre arrive, le dernier finit au bas de la page */
  let sommaire = null;
  const initSommaire = () => {
    const nav = document.querySelector('.cluutch-charly-sommaire');
    if (!nav) { sommaire = null; return; }
    const liens = [...nav.querySelectorAll('a[href^="#"]')];
    sommaire = {
      nav,
      liens,
      chapitres: liens.map((a) => document.getElementById(decodeURIComponent(a.getAttribute('href').slice(1)))),
      segments: [...nav.querySelectorAll('.cluutch-charly-progression i')],
      actif: -2
    };
  };
  const mesurerSommaire = () => {
    if (!sommaire) return;
    const marge = parseFloat(window.getComputedStyle(racine).scrollPaddingTop) || 0;
    const y = window.scrollY;
    const lecture = y + marge;
    const finPage = racine.scrollHeight - window.innerHeight + marge;
    const debuts = sommaire.chapitres.map((ch) => (ch
      ? ch.getBoundingClientRect().top + y - (parseFloat(window.getComputedStyle(ch).scrollMarginTop) || 0)
      : Infinity));
    let actif = -1;
    debuts.forEach((debut, k) => {
      const fin = k + 1 < debuts.length ? debuts[k + 1] : finPage;
      const p = Math.min(1, Math.max(0, (lecture - debut) / Math.max(1, fin - debut)));
      if (sommaire.segments[k]) sommaire.segments[k].style.transform = `scaleX(${p.toFixed(4)})`;
      if (lecture >= debut - 1) actif = k;   // 1 px de tolérance : arrondi du défilement
    });
    if (actif !== sommaire.actif) {
      sommaire.liens.forEach((a, k) => (k === actif ? a.setAttribute('aria-current', 'true') : a.removeAttribute('aria-current')));
      sommaire.nav.classList.toggle('a-actif', actif >= 0);
      sommaire.actif = actif;
    }
  };

  let prevu = false;
  const image = () => {
    prevu = false;
    mesurerSommaire();
    // au bas de la page, la marge basse de l'observateur n'est jamais franchie : tout ce qui reste paraît
    if (window.innerHeight + window.scrollY >= racine.scrollHeight - 2) toutReveler();
  };
  const planifier = () => { if (!prevu) { prevu = true; window.requestAnimationFrame(image); } };
  window.addEventListener('scroll', planifier, { passive: true });
  window.addEventListener('resize', planifier);
  window.addEventListener('load', planifier);
  initSommaire();
  planifier();

  /* 3. Bandeau des teintes : deux fois plus vif au plus quand on défile ; au survol ou en pause, il
        ralentit jusqu'à s'arrêter, puis repart en douceur */
  const initBandeau = (zone) => {
    if (zone.dataset.charlyPret) return;
    zone.dataset.charlyPret = '1';
    const piste = zone.querySelector('.cluutch-charly-bandeau__piste');
    const bouton = zone.querySelector('.cluutch-charly-bandeau__pause');
    if (!piste || calme.matches || !piste.animate) { if (bouton) bouton.hidden = true; return; }
    const anim = piste.animate([{ transform: 'translateX(0)' }, { transform: 'translateX(-50%)' }],
      { duration: 56000, iterations: Infinity });
    anim.pause();
    let visible = false;
    let survol = false;
    let arret = false;
    let elan = 0;
    let taux = 0;
    let tPrec = 0;
    let enMarche = false;
    const regler = (v) => (anim.updatePlaybackRate ? anim.updatePlaybackRate(v) : (anim.playbackRate = v));
    const pas = (t) => {
      const dt = tPrec ? Math.min(100, t - tPrec) : 16;
      tPrec = t;
      elan *= Math.exp(-dt / 400);
      const but = visible && !survol && !arret ? 1 + elan : 0;
      taux += (but - taux) * (1 - Math.exp(-dt / 160));
      if (but === 0 && taux < 0.02) { taux = 0; anim.pause(); enMarche = false; tPrec = 0; return; }
      if (anim.playState !== 'running') anim.play();
      regler(Math.max(0.02, taux));
      window.requestAnimationFrame(pas);
    };
    const relancer = () => { if (!enMarche) { enMarche = true; window.requestAnimationFrame(pas); } };
    new IntersectionObserver(([e]) => { visible = e.isIntersecting; relancer(); }).observe(zone);
    zone.addEventListener('pointerenter', (e) => { if (e.pointerType === 'mouse') { survol = true; relancer(); } });
    zone.addEventListener('pointerleave', (e) => { if (e.pointerType === 'mouse') { survol = false; relancer(); } });
    if (bouton) {
      bouton.addEventListener('click', () => {
        arret = !arret;
        bouton.setAttribute('aria-pressed', String(arret));
        relancer();
      });
    }
    let yPrec = window.scrollY;
    let tDefil = performance.now();
    window.addEventListener('scroll', () => {
      const t = performance.now();
      const v = Math.abs(window.scrollY - yPrec) / Math.max(16, t - tDefil);   // px par ms
      yPrec = window.scrollY;
      tDefil = t;
      if (!visible) return;
      elan = Math.min(1, Math.max(elan, v * 1.2));
      relancer();
    }, { passive: true });
  };

  /* 4. Film : le plan large bouge lentement tant qu'il est à l'écran */
  const initFilm = (image) => {
    if (image.dataset.charlyPret || calme.matches || !('IntersectionObserver' in window)) return;
    image.dataset.charlyPret = '1';
    new IntersectionObserver(([e]) => image.classList.toggle('en-vue', e.isIntersecting)).observe(image);
  };

  const initPortee = (portee) => {
    portee.querySelectorAll('.cluutch-charly-bandeau').forEach(initBandeau);
    portee.querySelectorAll('.cluutch-charly-film__image').forEach(initFilm);
  };
  initPortee(document);

  /* 5. Fenêtre vidéo, créée au premier tap */
  let lecteur = null;
  let source = null;
  const creerLecteur = () => {
    const d = document.createElement('dialog');
    d.className = 'cluutch-charly-lecteur';
    d.innerHTML = '<div class="cluutch-charly-lecteur__cadre"></div>'
      + '<button type="button" class="cluutch-charly-lecteur__fermer">'
      + '<svg viewBox="0 0 24 24" aria-hidden="true" focusable="false"><path d="M5 5l14 14M19 5 5 19"></path></svg></button>';
    document.body.appendChild(d);
    d.querySelector('.cluutch-charly-lecteur__fermer').addEventListener('click', () => d.close());
    d.addEventListener('click', (e) => { if (e.target === d) d.close(); });
    d.addEventListener('close', () => {
      const video = d.querySelector('video');
      if (video) {
        video.pause();
        video.querySelectorAll('source').forEach((s) => s.remove());
        video.removeAttribute('src');
        video.load();   // coupe le téléchargement en cours
      }
      d.querySelector('.cluutch-charly-lecteur__cadre').replaceChildren();
      if (source) source.focus({ preventScroll: true });
    });
    return d;
  };
  document.addEventListener('click', (e) => {
    const bouton = e.target.closest('[data-charly-video]');
    if (!bouton) return;
    const groupe = bouton.closest('[data-charly-videos]');
    const modeles = groupe ? [...groupe.querySelectorAll('template[data-charly-video-format]')] : [];
    const modele = modeles.find((t) => {
      const format = t.dataset.charlyVideoFormat;
      return format === 'all' || window.matchMedia(format).matches;
    }) || modeles[modeles.length - 1];
    if (!modele || typeof HTMLDialogElement === 'undefined') return;
    lecteur = lecteur || creerLecteur();
    source = bouton;
    const video = modele.content.querySelector('video').cloneNode(true);
    lecteur.querySelector('.cluutch-charly-lecteur__cadre').replaceChildren(video);
    lecteur.style.setProperty('--ratio', modele.dataset.ratio || '1.7778');
    // la fenêtre porte le nom de la vidéo (« Le film »), pas celui de l'action (« Voir le film »)
    lecteur.setAttribute('aria-label', bouton.dataset.charlyVideoTitre || bouton.getAttribute('aria-label') || bouton.textContent.trim());
    lecteur.querySelector('.cluutch-charly-lecteur__fermer').setAttribute('aria-label', bouton.dataset.charlyFermer || 'Fermer');
    lecteur.showModal();
    const essai = video.play();
    if (essai && essai.catch) essai.catch(() => {});
  });

  /* 6. Bouton d'achat avant la mise en vente (cluutch-charly-achat) : rien n'est ajouté au panier ;
     son nom accessible porte déjà la date, le libellé la montre 2,4 s */
  document.addEventListener('click', (e) => {
    const bouton = e.target.closest('.cluutch-charly-achat--avant');
    if (!bouton || !bouton.dataset.charlyMention) return;
    const texte = bouton.querySelector('.cluutch-charly-achat__texte');
    if (!texte.dataset.charlyLibelle) texte.dataset.charlyLibelle = texte.textContent;
    texte.textContent = bouton.dataset.charlyMention;
    window.clearTimeout(bouton.charlyMinuteur);
    bouton.charlyMinuteur = window.setTimeout(() => { texte.textContent = texte.dataset.charlyLibelle; }, 2400);
  });

  /* Éditeur de thème : une section rechargée est reprise (rien n'y est masqué) */
  if (editeur) {
    document.addEventListener('shopify:section:load', (e) => {
      initPortee(e.target);
      initSommaire();
      planifier();
    });
    document.addEventListener('shopify:section:unload', () => window.setTimeout(() => { initSommaire(); planifier(); }, 0));
  }
})();
